import { useEffect } from 'react'
import { Loader2, RotateCcw, Volume2, X } from 'lucide-react'
import type { ChatMessage } from './chat-api'
import { useReadAloud } from './use-read-aloud'
import { useWorkspaceRole } from '@/features/workspace/use-workspace-role'

interface ReadAloudProps {
  message: ChatMessage
}

/**
 * Kompakte Vorlese-Steuerung fuer abgeschlossene Assistant-Nachrichten.
 * Erscheint neben dem Kopieren-Button. Kein Autoplay: Die Wiedergabe startet
 * erst nach Klick auf "Vorlesen" bzw. ueber die nativen Audio-Controls.
 * Hinweis: Beim Klick werden die Nachrichtendaten zur Sprachsynthese an
 * Mistral (EU) gesendet.
 *
 * Viewer sehen die Steuerung deaktiviert (paid API — der Server bleibt
 * authoritative und antwortet 403).
 */
export function ReadAloud({ message }: ReadAloudProps) {
  const { state, audioUrl, errorMessage, audioRef, toggle, retry } = useReadAloud(message)
  const { role } = useWorkspaceRole()
  const isViewer = role === 'viewer'

  // Nach erfolgreichem Laden: Element anbinden, aber NICHT automatisch starten.
  // Cleanup captured das Element selbst — React setzt ref.current beim Unmount
  // auf null, bevor Effect-Cleanups laufen; ohne Capture wuerde die laufende
  // Wiedergabe beim Verlassen des Chats nicht pausiert/geloescht.
  useEffect(() => {
    const audio = audioRef.current
    if (audio && audioUrl) {
      audio.src = audioUrl
    }
    return () => {
      if (audio) {
        audio.pause()
        audio.removeAttribute('src')
        audio.load()
      }
    }
  }, [audioUrl, audioRef])

  // Wiedergabe stoppen, sobald die Audio-Quelle wechselt oder entfernt wird.
  useEffect(() => {
    if (!audioUrl && audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current.load()
    }
  }, [audioUrl, audioRef])

  if (state === 'idle' || state === 'loading') {
    return (
      <button
        type="button"
        data-testid="read-aloud-button"
        aria-label={state === 'loading' ? 'Vorlesen abbrechen' : 'Antwort vorlesen'}
        title={
          state === 'loading'
            ? 'Vorlesen abbrechen'
            : 'Antwort vorlesen - Audio wird bei Mistral (EU) erzeugt'
        }
        aria-busy={state === 'loading'}
        aria-live="polite"
        disabled={isViewer}
        onClick={toggle}
        className="mt-1.5 inline-flex h-11 min-[761px]:h-9 items-center gap-1 rounded px-2 text-[12px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {state === 'loading' ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            <span>Abbrechen</span>
          </>
        ) : (
          <>
            <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Vorlesen</span>
          </>
        )}
      </button>
    )
  }

  if (state === 'error') {
    return (
      <div className="mt-1.5 flex max-w-full flex-wrap items-center gap-1">
        <span role="status" aria-live="polite" className="px-1 text-[12px] text-destructive">
          {errorMessage ?? 'Vorlesen fehlgeschlagen'}
        </span>
        <button
          type="button"
          data-testid="read-aloud-retry"
          aria-label="Vorlesen erneut versuchen"
          title="Erneut versuchen"
          onClick={retry}
          className="inline-flex h-11 min-[761px]:h-9 items-center gap-1 rounded px-2 text-[12px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Erneut</span>
        </button>
      </div>
    )
  }

  return (
    <div className="mt-1.5 flex max-w-full flex-wrap items-center gap-2">
      <audio
        ref={audioRef}
        data-testid="read-aloud-audio"
        controls
        preload="none"
        className="h-11 min-[761px]:h-9 max-w-full"
        aria-label="Vorlesung der Antwort"
      />
      <button
        type="button"
        data-testid="read-aloud-stop"
        aria-label="Vorlesen stoppen"
        title="Stoppen"
        onClick={toggle}
        className="inline-flex h-11 min-[761px]:h-9 items-center gap-1 rounded px-2 text-[12px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Stoppen</span>
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        Wiedergabe bereit. Wiedergabe startet erst nach Klick auf Abspielen.
      </span>
    </div>
  )
}
